<!-- Modern Professional Header Navigation with Full Width -->
<nav class="modern-navbar">
  <div class="navbar-container">
    
    <!-- Left Section: Logo + Nav Icons + Breadcrumb -->
    <div class="navbar-left">

      <!-- Company Logo & Name -->
      <a class="company-brand" [routerLink]="['/modules']" aria-label="Home">
        <img src="assets/images/womensbestie.svg"
             alt="Womens Bestie Logo"
             class="company-logo">
        <span class="company-name d-none d-md-inline">Womens Bestie</span>
      </a>

      <!-- Divider -->
      <span class="nav-divider"></span>

      <!-- Modules Icon Button -->
      <button
        class="nav-icon-btn"
        [class.active]="(moduleStateService.sidebarMode$ | async) === 'modules'"
        (click)="onModulesClick()"
        matTooltip="All Modules"
        matTooltipPosition="below"
        type="button"
        aria-label="Toggle modules navigation">
        <mat-icon>apps</mat-icon>
        <span class="nav-icon-label">Modules</span>
      </button>

      <!-- Pages Icon Button -->
      <button
        class="nav-icon-btn"
        [class.active]="(moduleStateService.sidebarMode$ | async) === 'pages'"
        [class.nav-icon-disabled]="!isPagesEnabled"
        [disabled]="!isPagesEnabled"
        (click)="onPagesClick()"
        [matTooltip]="isPagesEnabled ? ('Pages – ' + selectedModule?.label) : 'Select a module first'"
        matTooltipPosition="below"
        type="button"
        aria-label="Toggle pages navigation">
        <mat-icon>menu_open</mat-icon>
        <span class="nav-icon-label">{{ selectedModule ? selectedModule.label : 'Pages' }}</span>
        <span *ngIf="selectedModule" class="active-module-dot"></span>
      </button>

      <!-- Dynamic Breadcrumb Navigation -->
      <div class="breadcrumb-section">
        <div class="breadcrumb-container">
          <!-- Multi-level breadcrumb (3+ levels) - Show all levels -->
          <ng-container *ngIf="breadcrumbs.length > 2">
            <!-- First parent level -->
            <span class="breadcrumb-parent fw-medium d-none d-lg-inline">
              {{ breadcrumbs[0] }}
            </span>
            <mat-icon class="breadcrumb-separator mx-2 d-none d-lg-inline" aria-hidden="true">
              chevron_right
            </mat-icon>

            <!-- Middle parent level -->
            <span class="breadcrumb-middle fw-medium d-none d-md-inline">
              {{ breadcrumbs[breadcrumbs.length - 2] }}
            </span>
            <mat-icon class="breadcrumb-separator mx-2 d-none d-md-inline" aria-hidden="true">
              chevron_right
            </mat-icon>

            <!-- Current page (child) -->
            <h1 class="page-title mb-0">
              {{ breadcrumbs[breadcrumbs.length - 1] }}
            </h1>
          </ng-container>
          
          <!-- Two-level breadcrumb (Parent => Child) like: Rewards => Bonus Amount -->
          <ng-container *ngIf="breadcrumbs.length === 2">
            <span class="breadcrumb-parent fw-medium d-none d-md-inline">
              {{ breadcrumbs[0] }}
            </span>
            <mat-icon class="breadcrumb-separator mx-2 d-none d-md-inline" aria-hidden="true">
              chevron_right
            </mat-icon>
            <h1 class="page-title mb-0">
              {{ breadcrumbs[1] }}
            </h1>
          </ng-container>
          
          <!-- Single level or fallback -->
          <ng-container *ngIf="breadcrumbs.length <= 1">
            <h1 class="page-title mb-0">
              {{ currentPageTitle }}
            </h1>
          </ng-container>
        </div>
      </div>
    </div>
    
    <!-- Right Section: Action Icons -->
    <div class="navbar-right">
      <!-- Shopping Cart Button -->
      <button 
        mat-icon-button 
        class="btn btn-link header-action-btn" 
        [routerLink]="['/products/cart']" 
        matTooltip="Shopping Cart"
        type="button"
        aria-label="Shopping Cart">
        <app-cart-badge></app-cart-badge>
      </button>
      
      <!-- User Profile Dropdown -->
      <div class="dropdown user-dropdown">
        <button 
          class="btn btn-link dropdown-toggle user-profile-btn" 
          id="userDropdown" 
          data-bs-toggle="dropdown" 
          aria-expanded="false"
          type="button"
          aria-label="User menu">
          
          <!-- User Avatar -->
          <div class="user-avatar">
            <!-- Show profile image if available -->
            <img *ngIf="profileImageUrl && !showInitials" 
                 [src]="profileImageUrl" 
                 [alt]="getUserDisplayName()" 
                 class="avatar-image"
                 (error)="onImageError($event)">
            <!-- Show default image if no profile image -->
            <img *ngIf="!profileImageUrl && !showInitials" 
                 [src]="defaultProfileImage" 
                 [alt]="getUserDisplayName()" 
                 class="avatar-image"
                 (error)="showInitials = true">
            <!-- Show initials as final fallback -->
            <span *ngIf="showInitials || (!profileImageUrl && !defaultProfileImage)" 
                  class="avatar-initials">{{ userInitials }}</span>
          </div>
          
          <!-- User Info (Hidden on mobile) -->
          <div class="user-info d-none d-lg-flex flex-column align-items-start">
            <span class="user-name">{{ getUserDisplayName() }}</span>
            <span class="user-id">{{ getUserId() }}</span>
          </div>
          
          <!-- Dropdown Icon -->
          <mat-icon class="dropdown-icon d-none d-lg-inline">expand_more</mat-icon>
        </button>
        
        <!-- Modern Dropdown Menu -->
        <ul class="dropdown-menu dropdown-menu-end user-dropdown-menu shadow-lg" 
            aria-labelledby="userDropdown">
          
          <!-- User Info Header (Mobile Only) -->
          <li class="d-lg-none user-header px-3 py-3">
            <div class="d-flex align-items-center gap-3">
              <div class="user-avatar large">
                <!-- Show profile image if available -->
                <img *ngIf="profileImageUrl && !showInitials" 
                     [src]="profileImageUrl" 
                     [alt]="getUserDisplayName()" 
                     class="avatar-image"
                     (error)="onImageError($event)">
                <!-- Show default image if no profile image -->
                <img *ngIf="!profileImageUrl && !showInitials" 
                     [src]="defaultProfileImage" 
                     [alt]="getUserDisplayName()" 
                     class="avatar-image"
                     (error)="showInitials = true">
                <!-- Show initials as final fallback -->
                <span *ngIf="showInitials || (!profileImageUrl && !defaultProfileImage)" 
                      class="avatar-initials">{{ userInitials }}</span>
              </div>
              <div>
                <div class="fw-bold text-dark">{{ getUserDisplayName() }}</div>
                <div class="text-muted small">{{ getUserId() }}</div>
              </div>
            </div>
          </li>
          <li class="d-lg-none"><hr class="dropdown-divider my-0"></li>
          
          <!-- Menu Items -->
          <li>
            <a class="dropdown-item d-flex align-items-center gap-3 py-2 px-3" 
               [routerLink]="['/profile']">
              <mat-icon class="menu-icon">person_outline</mat-icon>
              <span>My Profile</span>
            </a>
          </li>
          
          <li>
            <a class="dropdown-item d-flex align-items-center gap-3 py-2 px-3" 
               [routerLink]="['/change-password']">
              <mat-icon class="menu-icon">lock_outline</mat-icon>
              <span>Change Password</span>
            </a>
          </li>
          
          <li class="d-md-none">
            <a class="dropdown-item d-flex align-items-center gap-3 py-2 px-3" 
               href="#">
              <mat-icon class="menu-icon">help_outline</mat-icon>
              <span>Help & Support</span>
            </a>
          </li>
          
          <li><hr class="dropdown-divider my-2"></li>
          
          <!-- Theme Selector Section -->
          <li class="theme-selector-header px-3 py-2">
            <div class="d-flex align-items-center gap-2">
              <mat-icon class="theme-icon">{{ currentThemeIcon }}</mat-icon>
              <span class="theme-label">Theme</span>
              <span class="current-theme-badge">{{ getCurrentThemeDisplayName() }}</span>
            </div>
          </li>
          
          <!-- Theme Options Grid -->
          <li class="theme-options-container px-2 py-2">
            <div class="theme-grid">
              <button 
                *ngFor="let theme of availableThemes"
                class="theme-option-btn"
                [class.active]="currentTheme === theme.name"
                (click)="changeTheme(theme.name)"
                [title]="theme.description"
                type="button">
                <mat-icon class="theme-option-icon">{{ theme.icon }}</mat-icon>
                <span class="theme-option-name">{{ theme.displayName }}</span>
                <mat-icon *ngIf="currentTheme === theme.name" class="check-icon">check_circle</mat-icon>
              </button>
            </div>
          </li>
          
          <li><hr class="dropdown-divider my-2"></li>
          
          <li>
            <a class="dropdown-item logout-item d-flex align-items-center gap-3 py-2 px-3" 
               href="#" 
               (click)="logout(); $event.preventDefault()">
              <mat-icon class="menu-icon">logout</mat-icon>
              <span>Logout</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</nav>

